import { Subscription, filter } from 'rxjs';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import {
  insuranceClaimsTopic,
  insuranceOperationsTopic,
  LiveUpdateEventTypes,
  liveStatusTranslationKey,
  LiveUpdateConnectionState
} from '../../core/live-updates/live-update.models';

export type SortOrder = 1 | -1;

export function compareSortValues(left: unknown, right: unknown, order: SortOrder): number {
  if (left === right) {
    return 0;
  }

  if (left == null) {
    return -1 * order;
  }

  if (right == null) {
    return 1 * order;
  }

  if (typeof left === 'number' && typeof right === 'number') {
    return (left - right) * order;
  }

  return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) * order;
}

export function toggleSortField<T extends string>(
  field: T,
  currentField: T,
  currentOrder: SortOrder
): { field: T; order: SortOrder } {
  if (currentField === field) {
    return { field, order: currentOrder === 1 ? -1 : 1 };
  }

  return { field, order: 1 };
}

export function escapeCsvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

export function downloadCsv(filename: string, header: string[], rows: string[][]): void {
  const lines = rows.map(row => row.map(escapeCsvCell).join(','));
  const csv = [header.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function liveStatusKey(state: LiveUpdateConnectionState): string {
  return liveStatusTranslationKey(state);
}

export async function subscribeInsuranceOperationsLiveUpdates(
  tenantId: string,
  liveUpdateService: LiveUpdateService,
  onRefresh: () => void
): Promise<{ topic: string; subscription: Subscription; unsubscribe: () => Promise<void> }> {
  const topic = insuranceOperationsTopic(tenantId);
  await liveUpdateService.subscribe(topic);
  const subscription = liveUpdateService.events$
    .pipe(filter(event => event.eventType.startsWith('insurance.')))
    .subscribe(() => onRefresh());

  return {
    topic,
    subscription,
    unsubscribe: async () => {
      subscription.unsubscribe();
      await liveUpdateService.unsubscribe(topic);
    }
  };
}

export async function subscribeInsuranceClaimsLiveUpdates(
  tenantId: string,
  liveUpdateService: LiveUpdateService,
  onRefresh: () => void
): Promise<{ topic: string; subscription: Subscription; unsubscribe: () => Promise<void> }> {
  const topic = insuranceClaimsTopic(tenantId);
  await liveUpdateService.subscribe(topic);
  const subscription = liveUpdateService.events$
    .pipe(filter(event => event.eventType === LiveUpdateEventTypes.insuranceClaimsUpdated))
    .subscribe(() => onRefresh());

  return {
    topic,
    subscription,
    unsubscribe: async () => {
      subscription.unsubscribe();
      await liveUpdateService.unsubscribe(topic);
    }
  };
}

export const INSURANCE_LIST_TOOLBAR_STYLES = `
  .toolbar{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
  .live-status{margin-inline-start:auto;font-size:12px;color:#4b5563}
  .sort-btn{border:0;background:none;color:inherit;font:inherit;cursor:pointer;padding:0}
  .info{color:#1d4ed8}
`;
